import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useErrorToast } from "./App";
import type { Id } from "../../convex/_generated/dataModel";

export function UserManagementPage() {
  const allowedUsers = useQuery(api.users.listAllowedUsers, {});
  const addUser = useMutation(api.users.addAllowedUser);
  const removeUser = useMutation(api.users.removeAllowedUser);
  const showError = useErrorToast();
  const [email, setEmail] = useState("");
  const [removing, setRemoving] = useState<Id<"allowedUsers"> | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await addUser({ email: trimmed });
      setEmail("");
    } catch (err: any) {
      showError(err?.message ?? String(err));
    }
  };

  const handleRemove = async (id: Id<"allowedUsers">) => {
    setRemoving(id);
    try {
      await removeUser({ id });
    } catch (err: any) {
      showError(err?.message ?? String(err));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Allowed Users</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Manage who can access this dashboard.
          </p>
        </div>
      </div>

      <form onSubmit={(e) => void handleAdd(e)} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
        />
        <button
          type="submit"
          disabled={!email.trim()}
          className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white rounded-lg transition-colors"
        >
          Add user
        </button>
      </form>

      {allowedUsers === undefined ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : allowedUsers.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No allowed users yet.</p>
          <p className="text-sm text-zinc-500 mt-1">Add an email above to grant access.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {allowedUsers.map((u) => (
            <div
              key={u._id}
              className="flex items-center justify-between py-3 px-4 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors group"
            >
              <span className="text-sm text-zinc-300 truncate">{u.email}</span>
              <button
                onClick={() => void handleRemove(u._id)}
                disabled={removing === u._id}
                className="text-xs text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
              >
                {removing === u._id ? "Removing..." : "Remove"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
